import Link from "next/link";
import { blogPostUrl, type BlogCategory } from "@/lib/blog-config";

// Shared card renderer for the /blog index. Imported by BOTH the
// server page (app/(marketing)/blog/page.tsx, SSR grid) and the
// client load-more component (app/(marketing)/blog/LoadMore.tsx,
// appended grid) so the two grids can never drift visually — the
// previous implementation duplicated the markup in two files and
// required a "keep in sync" comment. No "use client" directive:
// the module is server-safe and only enters the client bundle via
// the LoadMore import.

export type BlogArticle = {
    id: string;
    slug: string;
    title: string;
    excerpt: string;
    category: BlogCategory;
    metaLine: string;
    featuredImageUrl: string | null;
};

// The API (/api/blog/list) and the server mapper both emit
// "… · 8 MIN READ". Display-only downcase so the card meta follows
// the brand's sentence-case rule without touching the data layer —
// the appended cards come straight from the read-only API response.
function displayMetaLine(metaLine: string): string {
    return metaLine.replace(/(\d+)\s+MIN READ$/, "$1 min read");
}

// Category-keyed fallback art for articles that don't have a
// featured_image_url yet (the gap between draft creation and image
// generation). Quiet line work in the brand palette: tertiary
// strokes, indigo accent, soft indigo fills.
function CategoryGlyph({ category }: { category: BlogCategory }) {
    switch (category) {
        case "AI Content":
            return (
                <svg width="120" height="68" viewBox="0 0 120 68" fill="none" aria-hidden="true">
                    <circle cx="60" cy="34" r="21" stroke="#9CA1A8" strokeWidth="1.5" />
                    <circle cx="60" cy="34" r="12" stroke="#3D5BDB" strokeWidth="1.5" />
                    <circle cx="60" cy="34" r="4" fill="#3D5BDB" />
                    <circle cx="30" cy="34" r="3" fill="#E4EAFB" stroke="#9CA1A8" strokeWidth="1" />
                    <circle cx="90" cy="34" r="3" fill="#E4EAFB" stroke="#9CA1A8" strokeWidth="1" />
                </svg>
            );
        case "Playbooks":
            return (
                <svg width="120" height="68" viewBox="0 0 120 68" fill="none" aria-hidden="true">
                    <rect x="20" y="14" width="36" height="40" rx="4" fill="#FFFFFF" stroke="#9CA1A8" strokeWidth="1.5" />
                    <rect x="64" y="14" width="36" height="40" rx="4" fill="#E4EAFB" stroke="#3D5BDB" strokeWidth="1.5" />
                    <line x1="28" y1="26" x2="48" y2="26" stroke="#EAEAE7" strokeWidth="2" strokeLinecap="round" />
                    <line x1="28" y1="34" x2="48" y2="34" stroke="#EAEAE7" strokeWidth="2" strokeLinecap="round" />
                    <line x1="28" y1="42" x2="40" y2="42" stroke="#EAEAE7" strokeWidth="2" strokeLinecap="round" />
                    <line x1="72" y1="26" x2="92" y2="26" stroke="#3D5BDB" strokeWidth="2" strokeLinecap="round" opacity="0.45" />
                    <line x1="72" y1="34" x2="92" y2="34" stroke="#3D5BDB" strokeWidth="2" strokeLinecap="round" opacity="0.45" />
                    <line x1="72" y1="42" x2="84" y2="42" stroke="#3D5BDB" strokeWidth="2" strokeLinecap="round" opacity="0.45" />
                </svg>
            );
        case "Case Studies":
            return (
                <svg width="120" height="68" viewBox="0 0 120 68" fill="none" aria-hidden="true">
                    <polyline points="12,52 32,44 52,46 72,30 92,24 108,12" stroke="#3D5BDB" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    <polyline points="12,56 32,53 52,50 72,45 92,41 108,36" stroke="#9CA1A8" strokeWidth="1.5" fill="none" strokeDasharray="3 4" strokeLinecap="round" />
                    <circle cx="108" cy="12" r="3.5" fill="#3D5BDB" />
                </svg>
            );
        case "Backlinks":
            return (
                <svg width="120" height="68" viewBox="0 0 120 68" fill="none" aria-hidden="true">
                    <circle cx="30" cy="34" r="10" fill="#FFFFFF" stroke="#9CA1A8" strokeWidth="1.5" />
                    <circle cx="90" cy="34" r="10" fill="#E4EAFB" stroke="#3D5BDB" strokeWidth="1.5" />
                    <path d="M40 34 Q60 16 80 34" stroke="#3D5BDB" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    <path d="M40 34 Q60 52 80 34" stroke="#9CA1A8" strokeWidth="1.5" fill="none" strokeDasharray="3 4" strokeLinecap="round" />
                </svg>
            );
        case "Integrations":
            return (
                <svg width="120" height="68" viewBox="0 0 120 68" fill="none" aria-hidden="true">
                    <rect x="22" y="22" width="24" height="24" rx="6" fill="#FFFFFF" stroke="#9CA1A8" strokeWidth="1.5" />
                    <rect x="74" y="22" width="24" height="24" rx="6" fill="#E4EAFB" stroke="#3D5BDB" strokeWidth="1.5" />
                    <line x1="46" y1="34" x2="74" y2="34" stroke="#3D5BDB" strokeWidth="1.5" />
                    <circle cx="60" cy="34" r="4" fill="#3D5BDB" />
                </svg>
            );
        default: // SEO + All
            return (
                <svg width="120" height="68" viewBox="0 0 120 68" fill="none" aria-hidden="true">
                    <rect x="12" y="44" width="14" height="14" rx="3" fill="#EAEAE7" />
                    <rect x="32" y="36" width="14" height="22" rx="3" fill="#E4EAFB" />
                    <rect x="52" y="26" width="14" height="32" rx="3" fill="#C9D4F4" />
                    <rect x="72" y="14" width="14" height="44" rx="3" fill="#3D5BDB" />
                    <rect x="92" y="22" width="14" height="36" rx="3" fill="#3350C4" />
                </svg>
            );
    }
}

export default function ArticleCard({ article, featured = false }: { article: BlogArticle; featured?: boolean }) {
    return (
        <Link
            href={blogPostUrl(article.slug)}
            className={featured ? "bl-card bl-card-featured" : "bl-card"}
        >
            <div className="bl-card-media">
                {article.featuredImageUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={article.featuredImageUrl} alt="" loading={featured ? "eager" : "lazy"} />
                ) : (
                    <CategoryGlyph category={article.category} />
                )}
            </div>
            <div className="bl-card-body">
                <div className="bl-card-tag">{article.category}</div>
                <h3 className="bl-card-title">{article.title}</h3>
                <p className="bl-card-excerpt">{article.excerpt}</p>
                <div className="bl-card-meta">{displayMetaLine(article.metaLine)}</div>
            </div>
        </Link>
    );
}
